import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, LinearProgress, MenuItem, TextField, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import RoundedPaper from 'shared/components/RoundedPaper';
import AgentHubService from 'services/AgentHubService';
import { fmtTime } from './parts';
import { QualityReport } from './types';

const service = new AgentHubService();
const fmt = (x: number | null) => (x == null ? '—' : x.toFixed(1));
const tone = (x: number | null) => (x == null ? undefined : x < 3 ? '#B91C1C' : x >= 4 ? '#065F46' : undefined);

// The periods the collector accepts (agentHub/scores.ts QUALITY_DAYS); the choice is remembered in this browser.
export const PERIODS = [
  { days: 7, label: '7 days', caption: 'last 7 days' },
  { days: 14, label: '2 weeks', caption: 'last 2 weeks' },
  { days: 30, label: '30 days', caption: 'last 30 days' },
  { days: 90, label: '90 days', caption: 'last 90 days' },
  { days: 180, label: '6 months', caption: 'last 6 months' },
] as const;
const DEFAULT_DAYS = 30;
const DAYS_KEY = 'agents.qualityDays';
const periodOf = (days: number) => PERIODS.find(p => p.days === days) ?? PERIODS.find(p => p.days === DEFAULT_DAYS)!;
function readDays(): number {
  try { return periodOf(Number(localStorage.getItem(DAYS_KEY))).days; } catch { return DEFAULT_DAYS; }
}

/** The Quality view: what the manager agents found, so a change to an agent can be judged before and after. */
export function QualityView({ report }: { report: QualityReport }) {
  const period = periodOf(report.days ?? DEFAULT_DAYS); // the period the report covers: a collector without ?days always sends 30
  if (!report.agents.length) return <Alert severity="info" icon={false}>No runs have been scored in the {period.caption}. A manager agent (dev-manager) scores its vertical's runs; it is a draft for now, so it runs only when someone asks it to.</Alert>;
  const weeks = report.agents[0].rubrics[0]?.weekly.map(w => w.weekStart) ?? [];
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Scores from 1 to 5, given by each vertical's manager agent on its rubrics, {period.caption}. Weeks with nothing scored are left out. Hover a week for the runs behind it. As of {fmtTime(report.at)}.
      </Typography>
      <RoundedPaper sx={{ mb: 2, overflowX: 'auto' }}>
        <Table size="small" sx={{ '& th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell>Agent</TableCell>
              <TableCell>Rubric</TableCell>
              <TableCell align="right">{period.label}</TableCell>
              {weeks.map(w => <TableCell key={w} align="right">Week of {w.slice(5)}</TableCell>)}
              <TableCell>Lowest scored runs</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.agents.flatMap(a => a.rubrics.map((r, i) => (
              <TableRow key={`${a.agent}:${r.rubric}`} sx={{ verticalAlign: 'top' }}>
                <TableCell>{i === 0 ? <RouterLink to={`/agents/${a.agent}`}>{a.agent}</RouterLink> : ''}</TableCell>
                <TableCell>{r.rubric}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: tone(r.avg) }}>{fmt(r.avg)} <Typography component="span" variant="caption" color="text.secondary">({r.runs})</Typography></TableCell>
                {r.weekly.map(w => <TableCell key={w.weekStart} align="right" sx={{ color: tone(w.avg) }}><Tooltip title={`${w.runs} run(s) scored`} arrow><span>{fmt(w.avg)}</span></Tooltip></TableCell>)}
                <TableCell>
                  {r.worst.filter(x => x.score <= 3).slice(0, 3).map(x => (
                    <Typography key={x.runId} variant="body2" component="div">
                      {x.score} · <RouterLink to={`/agents/${a.agent}?run=${encodeURIComponent(x.runId)}`}>{x.startedAt ? fmtTime(x.startedAt) : x.runId}</RouterLink>{x.subject ? ` · ${x.subject}` : ''}: {x.reason}
                    </Typography>
                  ))}
                </TableCell>
              </TableRow>
            )))}
          </TableBody>
        </Table>
      </RoundedPaper>
      {report.proposals.length > 0 && (
        <RoundedPaper>
          <Typography variant="subtitle1" sx={{ px: 2, pt: 1.5 }}>Proposals (compare the weeks before and after one was approved)</Typography>
          <Table size="small">
            <TableBody>
              {report.proposals.map(p => <TableRow key={p.id}><TableCell>{p.decidedAt ? fmtTime(p.decidedAt) : 'waiting'}</TableCell><TableCell>{p.status}</TableCell><TableCell>{p.agent}</TableCell><TableCell>{p.title}</TableCell></TableRow>)}
            </TableBody>
          </Table>
        </RoundedPaper>
      )}
    </>
  );
}

export default function QualityTab() {
  const [days, setDays] = useState<number>(readDays);
  const [report, setReport] = useState<QualityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let gone = false;
    setLoading(true);
    setError(null);
    service.quality(days)
      .then(r => { if (!gone) setReport(r); })
      .catch(e => { if (!gone) setError(e.message); })
      .finally(() => { if (!gone) setLoading(false); });
    return () => { gone = true; };
  }, [days]);
  const choose = (d: number) => {
    setDays(d);
    try { localStorage.setItem(DAYS_KEY, String(d)); } catch { /* private window: not remembered */ }
  };
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <TextField select size="small" id="quality-days" label="Time frame" value={days} onChange={e => choose(Number(e.target.value))} sx={{ minWidth: 160 }}>
          {PERIODS.map(p => <MenuItem key={p.days} value={p.days}>{p.label}</MenuItem>)}
        </TextField>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>Quality didn't load: {error}</Alert>}
      {!report && loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {report && (
        <Box sx={{ position: 'relative', opacity: loading ? 0.5 : 1, transition: 'opacity 150ms' }}>
          {loading && <LinearProgress sx={{ position: 'absolute', top: -8, left: 0, right: 0 }} />}
          <QualityView report={report} />
        </Box>
      )}
    </>
  );
}
